/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, MagnifyingGlass, MapPin, Clock, Phone, Storefront, CheckCircle, Moped,
  Tag, Package, NavigationArrow, SquaresFour, Pill, Heart, Baby, Drop, Leaf, DotsThree, Minus, Plus,
  ShoppingCart, Pill as PillIcon, Wheelchair, Virus, TestTube, FirstAid, Stethoscope, Truck,
} from "@phosphor-icons/react";
import { custApi } from "../../lib/customerApi";

const cardCls = "bg-white rounded-[20px] border border-[#F1EFEC] shadow-[0_6px_24px_rgba(17,24,39,0.05)]";
const TILE_ICON = { pharmacy: PillIcon, equipment_rental: Wheelchair, biohazard: Virus, lab: TestTube,
  doctor: Stethoscope, ambulance: Truck, first_aid: FirstAid };
const SWATCH = ["#0B2B66", "#15803D", "#DC2626", "#6D28D9", "#0E7490", "#B45309"];
export const PRODUCT_CATS = [
  ["prescription", "Prescription", Pill], ["otc", "OTC", Heart], ["baby_care", "Baby Care", Baby],
  ["personal_care", "Personal Care", Drop], ["wellness", "Wellness", Leaf], ["other", "More", DotsThree],
];
export const pretty = (s) => (s || "").split("_").filter(Boolean).map(w => w[0].toUpperCase() + w.slice(1)).join(" ");
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
export function openState(p) {
  const ist = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const days = (p.days_open || []).map(d => d.slice(0, 3).toLowerCase());
  const openDay = !days.length || days.includes(DAYS[ist.getDay()]);
  if (!openDay) return { label: "Closed today", open: false };
  if (!p.hours_open || !p.hours_close) return { label: "Open today", open: true, soft: true };
  const hm = `${String(ist.getHours()).padStart(2, "0")}:${String(ist.getMinutes()).padStart(2, "0")}`;
  const open = hm >= p.hours_open && hm <= p.hours_close;
  return { label: open ? "Open now" : hm < p.hours_open ? "Opens later" : "Closed now", open };
}

export function deliveryText(p) {
  if (p.travel_min != null) {
    const t = Math.round(p.travel_min);
    return `Delivery in ${t + 15}–${t + 30} mins`;
  }
  return `Delivery in ${p.delivery_time_text || "40–60 mins"}`;
}

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
          className={`h-10 px-4 rounded-full border text-[14px] whitespace-nowrap transition-colors ${on ? "border-[#DC2626] bg-[#FFF1F1] text-[#DC2626] font-semibold" : "border-[#E5E7EB] bg-white text-[#111827]"}`}>
    {children}
  </button>
);

function PartnerRow({ p, i }) {
  const st = openState(p);
  const km = p.road_distance_km ?? p.distance_km;
  return (
    <Link to={`/me/services/partner/${p.id}`} data-testid={`partner-card-${p.id}`}
          className={`${cardCls} block p-4 active:scale-[0.99] transition-transform me-fade-up`} style={{ animationDelay: `${i * 50}ms` }}>
      <div className="flex gap-3">
        <div className="w-14 h-14 rounded-2xl grid place-items-center text-white shrink-0" style={{ background: SWATCH[i % SWATCH.length] }}>
          <Storefront size={26} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0 font-display font-bold text-[16px] text-[#111827] leading-snug line-clamp-2">{p.name}</div>
            <span className="shrink-0 text-[13px] font-semibold text-[#DC2626] bg-[#FFF1F1] rounded-lg px-3 py-1 inline-flex items-center gap-1">View <ArrowRight size={12} weight="bold" /></span>
          </div>
          {p.home_delivery && (
            <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[#15803D] bg-[#ECFDF3] rounded-full px-2 py-0.5" data-testid={`partner-home-delivery-${p.id}`}>
              <CheckCircle size={12} weight="fill" /> Home delivery
            </span>
          )}
          {(p.address || p.business_name) && (
            <div className="flex items-center gap-1.5 text-[13px] text-[#6B7280] mt-0.5">
              <MapPin size={13} className="shrink-0" /><span className="truncate">{p.address || p.business_name}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-[13px] mt-0.5 min-w-0 whitespace-nowrap overflow-hidden">
            <Clock size={13} className="text-[#6B7280] shrink-0" />
            <span className={st.open ? "text-[#15803D] font-medium" : "text-[#9CA3AF]"} data-testid={`partner-open-${p.id}`}>{st.label}</span>
            {p.hours_open && <span className="text-[#6B7280] truncate">· {p.hours_open} – {p.hours_close}</span>}
          </div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-[#F3F4F6] grid grid-cols-2 items-center gap-2 text-[12px] text-[#374151]">
        <span className="inline-flex items-center gap-1.5 min-w-0" data-testid={`partner-delivery-${p.id}`}>
          <Moped size={16} className="text-[#DC2626] shrink-0" /><span className="truncate">{deliveryText(p)}</span>
        </span>
        <span className="inline-flex items-center gap-1.5 min-w-0 border-l border-[#F3F4F6] pl-2">
          {p.offer_text ? <Tag size={16} className="text-[#DC2626] shrink-0" /> : km != null ? <NavigationArrow size={16} className="text-[#DC2626] shrink-0" /> : <Package size={16} className="text-[#DC2626] shrink-0" />}
          <span className="truncate" data-testid={`partner-distance-${p.id}`}>
            {p.offer_text || p.highlight_text || (km != null ? `${km} km by road` : "Trusted partner")}
          </span>
        </span>
      </div>
    </Link>
  );
}

export function MeServicePartners() {
  const { subcategory } = useParams();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [gps, setGps] = useState(null);
  const [tile, setTile] = useState(null);
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [onlyDelivery, setOnlyDelivery] = useState(false);

  const load = async () => {
    try {
      const params = new URLSearchParams({ subcategory });
      if (q) params.set("q", q);
      if (gps) { params.set("near_lat", gps.lat); params.set("near_lng", gps.lng); }
      const r = await custApi.get(`/me/partners?${params}`);
      setList(r.data);
    } catch (e) { setList({ partners: [] }); toast.error(e?.response?.data?.detail || "Failed to load partners"); }
  };
  useEffect(() => {
    custApi.get("/me/services-overview")
      .then(r => setTile((r.data.tiles || []).find(x => x.subcategory === subcategory))).catch(() => {});
  }, [subcategory]);
  useEffect(() => { load(); }, [subcategory, gps]);

  const requestGps = () => {
    if (!navigator.geolocation) { toast.error("Location not supported on this browser"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => toast.error(`Location denied: ${err.message}`));
  };
  const rows = (list?.partners || []).filter(p => (!onlyOpen || openState(p).open) && (!onlyDelivery || p.home_delivery));
  const Icon = TILE_ICON[subcategory] || Storefront;
  const label = tile?.label || pretty(subcategory);

  return (
    <div className="px-4 pt-3 pb-6 space-y-4" data-testid="me-service-partners">
      <Link to="/me/services" className="inline-flex items-center gap-2 text-[15px] text-[#374151]" data-testid="partners-back">
        <ArrowLeft size={16} /> Back to all services
      </Link>
      <header className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-[20px] bg-[#FFF1F1] text-[#DC2626] grid place-items-center shrink-0"><Icon size={30} weight="duotone" /></div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#6B7280]">Rescigo · {pretty(tile?.category) || "Service"}</div>
          <h1 className="font-display font-black text-[28px] leading-tight text-[#111827]" data-testid="partners-title">{label}</h1>
          {tile?.blurb && <p className="text-[14px] text-[#6B7280]">{tile.blurb}</p>}
        </div>
      </header>
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
        <div className="relative flex-1 min-w-0">
          <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="partner-search"
                 placeholder={`Search ${label.toLowerCase()} by name`}
                 className="w-full h-12 pl-11 pr-3 rounded-2xl border border-[#E5E7EB] bg-white text-[15px] outline-none focus:border-[#DC2626]" />
        </div>
        <button type="submit" className="h-12 px-5 rounded-2xl bg-[#DC2626] text-white font-semibold text-[15px]" data-testid="partner-search-btn">Search</button>
      </form>
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 no-scrollbar" data-testid="partner-filters">
        <Chip on={!!gps} onClick={requestGps} testid="use-location">
          <span className="inline-flex items-center gap-1.5"><MapPin size={15} weight={gps ? "fill" : "regular"} />{gps ? "Nearest first" : "Sort by nearest"}</span>
        </Chip>
        <Chip on={onlyOpen} onClick={() => setOnlyOpen(v => !v)} testid="filter-open-now">Open now</Chip>
        <Chip on={onlyDelivery} onClick={() => setOnlyDelivery(v => !v)} testid="filter-home-delivery">Home delivery</Chip>
      </div>
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-[18px] text-[#111827]" data-testid="partners-count">Available near you ({rows.length})</h2>
      </div>
      {!list ? (
        <div className="space-y-3">{[0, 1, 2].map(i => <div key={i} className="h-32 rounded-[20px] bg-[#F3F4F6] animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className={`${cardCls} p-8 text-center text-[14px] text-[#6B7280]`} data-testid="partners-empty">
          {list.partners.length ? "No partners match these filters." : "No partners listed in your area yet. Check back soon."}
        </div>
      ) : (
        <div className="space-y-3">{rows.map((p, i) => <PartnerRow key={p.id} p={p} i={i} />)}</div>
      )}
    </div>
  );
}

function Stepper({ qty, setQty, max, testid }) {
  return (
    <div className="h-10 rounded-xl border border-[#DC2626] flex items-center" data-testid={testid}>
      <button type="button" onClick={() => setQty(qty - 1)} className="w-10 h-full grid place-items-center text-[#DC2626]" data-testid={`${testid}-minus`}><Minus size={16} weight="bold" /></button>
      <span className="w-6 text-center font-semibold text-[15px]" data-testid={`${testid}-qty`}>{qty}</span>
      <button type="button" disabled={max != null && qty >= max} onClick={() => setQty(qty + 1)} className="w-10 h-full grid place-items-center text-[#DC2626] disabled:opacity-30" data-testid={`${testid}-plus`}><Plus size={16} weight="bold" /></button>
    </div>
  );
}

function ProductRow({ prod, qty, setQty }) {
  const isRental = prod.type === "rental";
  const out = prod.stock === 0;
  const cat = PRODUCT_CATS.find(c => c[0] === prod.product_category);
  return (
    <div className={`${cardCls} p-3 flex gap-3`} data-testid={`product-card-${prod.id}`}>
      <div className="w-[88px] h-[88px] rounded-2xl bg-[#F8FAFC] overflow-hidden grid place-items-center shrink-0">
        {prod.image_url ? <img src={prod.image_url} alt={prod.name} className="w-full h-full object-contain" loading="lazy" />
          : <Package size={34} weight="duotone" className="text-[#CBD5E1]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-[15px] text-[#111827] leading-tight">{prod.name}</div>
        <div className="text-[12px] text-[#6B7280] mt-0.5 truncate">
          {[prod.pack_info || (prod.unit && `per ${prod.unit}`), prod.description].filter(Boolean).join(" · ")}
        </div>
        {cat && <span className="inline-block mt-1 text-[11px] bg-[#F3F4F6] text-[#374151] rounded-md px-2 py-0.5">{cat[1]}</span>}
        <div className="mt-1 font-display font-black text-[17px] text-[#111827]">
          {isRental ? <>{inr(prod.daily_rate)}<span className="text-[12px] font-normal text-[#6B7280]">/day</span></> : inr(prod.price)}
        </div>
        {isRental && prod.deposit > 0 && <div className="text-[11px] text-[#6B7280]">Deposit {inr(prod.deposit)}</div>}
      </div>
      <div className="flex flex-col justify-center shrink-0">
        {out ? <span className="text-[12px] text-[#9CA3AF]">Out of stock</span>
          : qty > 0 ? <Stepper qty={qty} setQty={setQty} max={prod.stock} testid={`stepper-${prod.id}`} />
          : <button type="button" onClick={() => setQty(1)} data-testid={`order-${prod.id}`}
                    className="h-10 px-6 rounded-xl bg-[#FFF1F1] text-[#DC2626] font-semibold text-[15px]">{isRental ? "Rent" : "Add"}</button>}
      </div>
    </div>
  );
}

export function MePartnerDetail({ CheckoutModal }) {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [products, setProducts] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [cart, setCart] = useState({});
  const [checkout, setCheckout] = useState(false);

  useEffect(() => {
    custApi.get(`/me/partners/${id}`).then(r => setP(r.data)).catch(e => setErr(e?.response?.data?.detail || "Could not load partner"));
    custApi.get(`/me/partners/${id}/products`).then(r => setProducts(r.data.products || [])).catch(() => setProducts([]));
  }, [id]);

  const cats = useMemo(() => PRODUCT_CATS.filter(c => (products || []).some(x => (x.product_category || "other") === c[0])), [products]);
  const shown = (products || []).filter(x => (!cat || (x.product_category || "other") === cat)
    && (!q.trim() || `${x.name} ${x.description || ""}`.toLowerCase().includes(q.trim().toLowerCase())));
  const setQty = (pid) => (n) => setCart(c => { const nx = { ...c }; if (n <= 0) delete nx[pid]; else nx[pid] = n; return nx; });
  const lines = (products || []).filter(x => cart[x.id]).map(x => ({ product: x, qty: cart[x.id] }));
  const count = lines.reduce((s, l) => s + l.qty, 0);
  const total = lines.reduce((s, l) => s + l.qty * ((l.product.type === "rental" ? l.product.daily_rate : l.product.price) || 0), 0);
  useEffect(() => {
    document.body.classList.toggle("me-cart-open", count > 0);
    return () => document.body.classList.remove("me-cart-open");
  }, [count]);

  if (err) return <div className="px-4 pt-6 text-center text-[14px] text-[#6B7280]" data-testid="partner-error">{err}</div>;
  if (!p) return <div className="px-4 pt-4 space-y-3">{[0, 1, 2].map(i => <div key={i} className="h-28 rounded-[20px] bg-[#F3F4F6] animate-pulse" />)}</div>;
  const st = openState(p);
  const km = p.road_distance_km;

  return (
    <div className={`px-4 pt-3 space-y-4 ${count ? "pb-32" : "pb-6"}`} data-testid="me-partner-detail">
      <Link to={`/me/services/${p.subcategory}`} className="inline-flex items-center gap-2 text-[15px] text-[#374151]" data-testid="partner-back">
        <ArrowLeft size={16} /> Back to {pretty(p.subcategory).toLowerCase()} partners
      </Link>
      <header className={`${cardCls} p-4`} data-testid="partner-header">
        <div className="flex gap-3">
          <div className="w-16 h-16 rounded-2xl bg-[#0B2B66] text-white grid place-items-center shrink-0"><Storefront size={30} weight="duotone" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280]">{pretty(p.category)} · {pretty(p.subcategory)}</div>
            <h1 className="font-display font-black text-[22px] leading-tight text-[#111827]">{p.name}</h1>
            {p.address && <div className="flex items-center gap-1.5 text-[13px] text-[#6B7280] mt-0.5"><MapPin size={13} className="shrink-0" /><span className="truncate">{p.address}</span></div>}
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-[#F3F4F6] flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px]">
          <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-[#6B7280]" />
            <span className={st.open ? "text-[#15803D] font-medium" : "text-[#9CA3AF]"}>{st.label}</span>
            {p.hours_open && <span className="text-[#374151]">· {p.hours_open} – {p.hours_close}</span>}
          </span>
          {p.phone && <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1.5 text-[#374151]" data-testid="partner-call"><Phone size={14} />{p.phone}</a>}
          {p.home_delivery && <span className="inline-flex items-center gap-1 text-[12px] font-medium text-[#15803D] bg-[#ECFDF3] rounded-full px-2 py-0.5"><CheckCircle size={12} weight="fill" /> Home delivery</span>}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#374151]">
          <span className="inline-flex items-center gap-1.5" data-testid="partner-detail-delivery"><Moped size={15} className="text-[#DC2626]" />{deliveryText(p)}</span>
          {km != null && <span className="inline-flex items-center gap-1.5" data-testid="partner-detail-distance"><NavigationArrow size={15} className="text-[#DC2626]" />{km} km by road</span>}
          {p.offer_text && <span className="inline-flex items-center gap-1.5"><Tag size={15} className="text-[#DC2626]" />{p.offer_text}</span>}
        </div>
      </header>

      <div className="flex gap-2">
        <div className="relative flex-1 min-w-0">
          <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="product-search" placeholder="Search products or brands"
                 className="w-full h-12 pl-11 pr-3 rounded-2xl border border-[#E5E7EB] bg-white text-[15px] outline-none focus:border-[#DC2626]" />
        </div>
      </div>

      {cats.length > 1 && (
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 no-scrollbar" data-testid="product-cats">
          {[["", "All", SquaresFour], ...cats].map(([k, l, I]) => (
            <button key={k || "all"} type="button" onClick={() => setCat(k)} data-testid={`product-cat-${k || "all"}`}
                    className={`w-[78px] shrink-0 h-[78px] rounded-2xl border flex flex-col items-center justify-center gap-1 text-[12px] transition-colors ${cat === k ? "border-[#DC2626] bg-[#FFF1F1] text-[#DC2626] font-semibold" : "border-[#E5E7EB] bg-white text-[#374151]"}`}>
              <I size={24} />{l}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-[18px] text-[#111827]">{cat ? PRODUCT_CATS.find(c => c[0] === cat)?.[1] : "Popular products"}</h2>
        <Link to="/me/orders" className="text-[13px] font-semibold text-[#DC2626] inline-flex items-center gap-1" data-testid="partner-my-orders">My orders <ArrowRight size={12} weight="bold" /></Link>
      </div>
      {products === null ? <div className="h-28 rounded-[20px] bg-[#F3F4F6] animate-pulse" />
        : shown.length === 0 ? (
          <div className={`${cardCls} p-8 text-center text-[14px] text-[#6B7280]`} data-testid="catalog-empty">
            {products.length ? "No products match your search." : `${p.name} hasn't published listings yet. Check back soon.`}
          </div>
        ) : (
          <div className="space-y-3" data-testid="partner-catalog">
            {shown.map(prod => <ProductRow key={prod.id} prod={prod} qty={cart[prod.id] || 0} setQty={setQty(prod.id)} />)}
          </div>
        )}

      {count > 0 && (
        <div className="fixed z-40 bottom-[92px] inset-x-0 flex justify-center px-3 pointer-events-none" data-testid="cart-bar-wrap">
        <div className="pointer-events-auto w-full max-w-[436px] rounded-[22px] bg-[#DC2626] text-white px-4 py-3 flex items-center gap-3 shadow-[0_14px_30px_rgba(220,38,38,0.35)] me-fade-up" data-testid="cart-bar">
          <div className="relative"><ShoppingCart size={28} />
            <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white text-[#DC2626] text-[11px] font-bold grid place-items-center" data-testid="cart-count">{count}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-[20px] leading-tight" data-testid="cart-total">{inr(total)}</div>
            <div className="text-[12px] opacity-90">{count} item{count > 1 ? "s" : ""}</div>
          </div>
          <button onClick={() => setCheckout(true)} className="h-11 px-5 rounded-xl bg-white text-[#DC2626] font-bold text-[15px] inline-flex items-center gap-1.5" data-testid="cart-view">
            View Cart <ArrowRight size={14} weight="bold" />
          </button>
        </div>
        </div>
      )}
      {checkout && <CheckoutModal lines={lines} partner={p} onClose={() => setCheckout(false)}
                                  onQty={(pid, n) => setQty(pid)(n)} />}
    </div>
  );
}
